/* Inoutflooring Tile Calculator — Shopify section script.
   Auto-initializes every [data-if-calc] root. Each instance is independent,
   so the section can be dropped onto a page multiple times.

   Differences vs the Hockey Revolution version:
   - Color option awareness (filters variants, recolors preview).
   - Combo packs ("8+8 Pack" → 16, "15+15 Pack" → 30).
   - Numbered tiles in the SVG preview, restarting per pack.
   - Two-tone rendering when a variant is a Mix (alternates colors). */

(function () {
  'use strict';

  if (window.__IF_TILE_CALC_LOADED__) return;
  window.__IF_TILE_CALC_LOADED__ = true;

  var M_TO_FT = 3.28084;

  // Common color name → hex (best-effort). Anything missing falls back to the
  // raw name (so CSS named colors like 'red', 'orange' just work).
  var COLOR_HEX = {
    black: '#1a1a1a',
    white: '#fafafa',
    grey: '#7a7a7a',
    gray: '#7a7a7a',
    anthracite: '#2c2f33',
    red: '#d12127',
    blue: '#1f6feb',
    'navy': '#0b2545',
    teal: '#14b8a6',
    green: '#2da44e',
    yellow: '#ffd33d',
    orange: '#fb8500',
    pink: '#ff6db8',
    purple: '#7c3aed',
    brown: '#7c4a25'
  };

  function colorToHex(name) {
    if (!name) return '#888';
    var key = String(name).toLowerCase().trim();
    if (COLOR_HEX[key]) return COLOR_HEX[key];
    // Single-word lookup: try last word (e.g. 'light blue' → 'blue').
    var last = key.split(/\s+/).pop();
    if (COLOR_HEX[last]) return COLOR_HEX[last];
    return key;
  }

  function splitColorName(name) {
    if (!name) return [];
    return String(name).split(/\s*[&+\/]\s*/).map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function parsePackSize(title) {
    if (!title) return null;
    var s = String(title);
    // "X tiles" or "X tile" — strongest signal.
    var m = s.match(/(\d+)\s*tiles?/i);
    if (m) return parseInt(m[1], 10);
    // "X+X" combo pack.
    var m2 = s.match(/(\d+)\s*\+\s*(\d+)/);
    if (m2) return parseInt(m2[1], 10) + parseInt(m2[2], 10);
    // Fallback: first integer in the string.
    m = s.match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  }

  function isMixPackTitle(title) {
    return /\d+\s*\+\s*\d+/.test(String(title || ''));
  }

  function parseOverrideBlob(blob) {
    var byId = {};
    var byTitle = {};
    if (!blob) return { byId: byId, byTitle: byTitle };
    String(blob).split(/\r?\n/).forEach(function (line) {
      var parts = line.split(/[:=]/);
      if (parts.length < 2) return;
      var key = parts[0].trim();
      var val = parseInt(parts[1].trim(), 10);
      if (!key || !val || val <= 0) return;
      if (/^\d+$/.test(key)) byId[key] = val;
      else byTitle[key.toLowerCase()] = val;
    });
    return { byId: byId, byTitle: byTitle };
  }

  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    var formatted;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { formatted = window.Shopify.formatMoney(cents, format); }
      catch (e) { formatted = null; }
    }
    if (!formatted) {
      var amount = cents / 100;
      var p = {
        amount: amount.toFixed(2),
        amount_no_decimals: Math.round(amount).toString(),
        amount_with_comma_separator: amount.toFixed(2).replace('.', ','),
        amount_no_decimals_with_comma_separator: Math.round(amount).toString(),
        amount_with_space_separator: amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
        amount_with_period_and_space_separator: amount.toFixed(2)
      };
      formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, k) { return p[k] != null ? p[k] : m; });
    }
    // Strip HTML tags — many shop money_formats include <span class="money"> wrappers.
    return String(formatted).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  function init(root) {
    if (root.getAttribute('data-if-calc-ready') === '1') return;
    root.setAttribute('data-if-calc-ready', '1');

    var tileCm = parseFloat(root.getAttribute('data-tile-size-cm')) || 50;
    var tileM = tileCm / 100;
    var defaultUnit = root.getAttribute('data-default-unit') === 'imperial' ? 'imperial' : 'metric';
    var cartAction = root.getAttribute('data-cart-action') || 'redirect';

    var configEl = root.querySelector('[data-if-config]');
    var config = { variants: [], options: [], moneyFormat: '${{amount}}' };
    if (configEl) {
      try { config = JSON.parse(configEl.textContent); }
      catch (e) { console.warn('[if-calc] could not parse config JSON', e); }
    }

    var overrides = parseOverrideBlob(root.getAttribute('data-variant-overrides'));

    // Identify the option index that holds the color (if any).
    var colorOptionIdx = -1;
    (config.options || []).forEach(function (o, i) {
      if (o && /color|colour/i.test(o.name || '')) colorOptionIdx = i;
    });

    // Build the universe of available variants enriched with parsed pack size.
    var allVariants = (config.variants || []).map(function (v) {
      var size =
        overrides.byId[String(v.id)] ||
        overrides.byTitle[String(v.title || '').toLowerCase()] ||
        parsePackSize(v.title);
      var color = colorOptionIdx >= 0 ? (v.options[colorOptionIdx] || '') : '';
      return {
        id: v.id,
        title: v.title,
        price: v.price,
        priceFormatted: v.priceFormatted,
        available: !!v.available,
        size: size,
        color: color,
        isMix: isMixPackTitle(v.title)
      };
    }).filter(function (v) { return v.size && v.size > 0; });

    // Group by color → list of distinct color values that have at least one
    // available variant. We keep colors with all-sold-out variants out of the
    // picker so customers don't choose dead ends.
    var colors = [];
    var seen = {};
    allVariants.forEach(function (v) {
      if (!v.available) return;
      if (!v.color || seen[v.color]) return;
      seen[v.color] = true;
      colors.push(v.color);
    });

    var defaultWidth = parseFloat(root.getAttribute('data-default-width')) || 3;
    var defaultLength = parseFloat(root.getAttribute('data-default-length')) || 3;
    if (defaultUnit === 'imperial') {
      defaultWidth = +(defaultWidth * M_TO_FT).toFixed(2);
      defaultLength = +(defaultLength * M_TO_FT).toFixed(2);
    }

    var state = {
      unit: defaultUnit,
      width: defaultWidth,
      length: defaultLength,
      color: colors[0] || null,
      // null = follow optimizer; an object = manual override per pack size.
      manualOverride: null
    };

    // Stash the most recent optimizer recommendation so the +/- handler can
    // seed the manual override from it the first time the user clicks.
    var lastRecommendation = {};
    // Most recent computed packs (after applying manual override). Used by onCta.
    var currentActivePacks = [];

    var $ = function (s) { return root.querySelector(s); };
    var $$ = function (s) { return root.querySelectorAll(s); };

    var el = {
      unitBtns: $$('.if-calc__unit-btn'),
      unitLabels: $$('[data-unit-label]'),
      width: $('[data-if-width]'),
      length: $('[data-if-length]'),
      presets: $$('[data-preset]'),
      presetGroups: $$('[data-presets-for]'),
      colorGrid: $('[data-if-color-grid]'),
      colorHint: $('[data-if-color-hint]'),
      canvas: $('[data-if-canvas]'),
      vizDim: $('[data-if-viz-dim]'),
      vizGrid: $('[data-if-viz-grid]'),
      legend: $('[data-if-legend]'),
      totalTiles: $('[data-if-total-tiles]'),
      totalExtra: $('[data-if-total-extra]'),
      area: $('[data-if-area]'),
      areaUnit: $('[data-if-area-unit]'),
      grid: $('[data-if-grid]'),
      boxList: $('[data-if-box-list]'),
      priceTotal: $('[data-if-price-total]'),
      priceTotalValue: $('[data-if-price-total-value]'),
      footnote: $('[data-if-footnote]'),
      cta: $('[data-if-cta]'),
      error: $('[data-if-error]'),
      reset: $('[data-if-reset]')
    };

    if (el.width) el.width.value = state.width;
    if (el.length) el.length.value = state.length;
    el.unitLabels.forEach(function (u) { u.textContent = state.unit === 'metric' ? 'm' : 'ft'; });
    el.unitBtns.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-unit') === state.unit); });
    if (el.width) el.width.step = state.unit === 'metric' ? '0.1' : '0.5';
    if (el.length) el.length.step = state.unit === 'metric' ? '0.1' : '0.5';

    // Render color swatches.
    function renderSwatches() {
      if (!el.colorGrid) return;
      if (colors.length === 0) {
        el.colorGrid.innerHTML = '<div class="if-calc__note">No colors available — pick a product in the section settings.</div>';
        return;
      }
      var html = '';
      colors.forEach(function (c) {
        var hexes = splitColorName(c).map(colorToHex);
        var bg;
        if (hexes.length === 0) bg = '#888';
        else if (hexes.length === 1) bg = hexes[0];
        else bg = 'linear-gradient(135deg, ' + hexes[0] + ' 50%, ' + hexes[1] + ' 50%)';
        var active = c === state.color ? ' is-active' : '';
        html += '<button type="button" class="if-calc__swatch' + active + '" data-color="' + c.replace(/"/g, '&quot;') + '"' +
          ' title="' + c.replace(/"/g, '&quot;') + '"' +
          ' style="background:' + bg + ';">' +
          '<span>' + c + '</span></button>';
      });
      el.colorGrid.innerHTML = html;
      el.colorGrid.querySelectorAll('.if-calc__swatch').forEach(function (b) {
        b.addEventListener('click', function () { onColorChange(b.getAttribute('data-color')); });
      });
    }

    function activeVariantsForColor() {
      if (!state.color) return allVariants.filter(function (v) { return v.available; });
      return allVariants.filter(function (v) { return v.available && v.color === state.color; });
    }

    function packsForCurrentColor() {
      var arr = activeVariantsForColor().slice();
      // Deduplicate by size — keep the first encountered.
      var seenSizes = {};
      var packs = [];
      arr.forEach(function (v) {
        if (seenSizes[v.size]) return;
        seenSizes[v.size] = true;
        packs.push(v);
      });
      packs.sort(function (a, b) { return b.size - a.size; });
      return packs;
    }

    function getTileGrid() {
      var w = Math.max(0, parseFloat(state.width) || 0);
      var l = Math.max(0, parseFloat(state.length) || 0);
      var widthM, lengthM;
      if (state.unit === 'metric') { widthM = w; lengthM = l; }
      else { widthM = w / M_TO_FT; lengthM = l / M_TO_FT; }
      var cols = Math.max(1, Math.round(widthM / tileM));
      var rows = Math.max(1, Math.round(lengthM / tileM));
      return { cols: cols, rows: rows, total: cols * rows };
    }

    function findBestCombo(tilesNeeded, packs) {
      if (tilesNeeded <= 0 || packs.length === 0) {
        return { combo: {}, packsUsed: [], totalTiles: 0, boxes: 0, price: 0, waste: 0 };
      }
      var sizes = packs.map(function (p) { return p.size; });
      var packBySize = {};
      packs.forEach(function (p) { packBySize[p.size] = p; });

      var maxSize = Math.max.apply(null, sizes);
      var MAX = tilesNeeded + maxSize;

      var dp = new Array(MAX + 1).fill(null);
      var zero = {};
      sizes.forEach(function (s) { zero[s] = 0; });
      dp[0] = { boxes: 0, combo: zero, tiles: 0, price: 0 };

      for (var i = 1; i <= MAX; i++) {
        for (var k = 0; k < sizes.length; k++) {
          var s = sizes[k];
          if (i - s >= 0 && dp[i - s]) {
            var prev = dp[i - s];
            var nc = {};
            for (var key in prev.combo) nc[key] = prev.combo[key];
            nc[s] = (nc[s] || 0) + 1;
            var cand = {
              boxes: prev.boxes + 1,
              combo: nc,
              tiles: prev.tiles + s,
              price: prev.price + (packBySize[s] ? packBySize[s].price : 0)
            };
            if (!dp[i] || cand.boxes < dp[i].boxes) dp[i] = cand;
          }
        }
      }

      var best = null;
      for (var j = tilesNeeded; j <= MAX; j++) {
        if (!dp[j]) continue;
        var c = { combo: dp[j].combo, boxes: dp[j].boxes, price: dp[j].price, totalTiles: j, waste: j - tilesNeeded };
        if (!best) { best = c; continue; }
        if (c.waste < best.waste) best = c;
        else if (c.waste === best.waste) {
          if (c.boxes < best.boxes) best = c;
          else if (c.boxes === best.boxes && c.price < best.price) best = c;
        }
      }
      if (!best) return { combo: zero, packsUsed: [], totalTiles: 0, boxes: 0, price: 0, waste: 0 };

      // Expand combo to a flat list of pack instances in size-desc order
      // (largest first). This drives both the box list and the tile labels.
      var packsUsed = [];
      var orderedSizes = sizes.slice().sort(function (a, b) { return b - a; });
      orderedSizes.forEach(function (s) {
        var qty = best.combo[s] || 0;
        for (var n = 0; n < qty; n++) {
          packsUsed.push(packBySize[s]);
        }
      });
      best.packsUsed = packsUsed;
      return best;
    }

    function tileColorPalette() {
      // Returns 1- or 2-element array of hex codes for the chosen color.
      if (!state.color) return ['#cccccc'];
      var parts = splitColorName(state.color);
      if (parts.length === 0) return ['#cccccc'];
      return parts.map(colorToHex);
    }

    function renderCanvas(cols, rows, packsUsed) {
      if (!el.canvas) return;
      var VB_W = 400;
      var MAIN_H = 280;
      var PAD = 14;

      // Compute purchased vs. needed so we can lay out a spares strip below.
      var totalNeeded = cols * rows;
      var totalPurchased = packsUsed.reduce(function (s, p) { return s + (p.size || 0); }, 0);
      var spareCount = Math.max(0, totalPurchased - totalNeeded);

      var availW = VB_W - PAD * 2;
      var availH = MAIN_H - PAD * 2;
      var tileSize = Math.min(availW / cols, availH / rows);
      var gridW = tileSize * cols;
      var gridH = tileSize * rows;
      var offsetX = (VB_W - gridW) / 2;
      var offsetY = (MAIN_H - gridH) / 2;

      // Spares strip layout.
      var sparesH = 0;
      var spareTileSize = 0;
      var spareOffsetX = 0;
      var spareOffsetY = 0;
      var spareLabelY = 0;
      var spareCols = 0;
      if (spareCount > 0) {
        spareTileSize = Math.min(28, tileSize);
        // Wrap if there are more spares than fit on one row.
        var maxPerRow = Math.max(1, Math.floor((VB_W - PAD * 2 - 80) / (spareTileSize + 2)));
        spareCols = Math.min(spareCount, maxPerRow);
        var spareRows = Math.ceil(spareCount / spareCols);
        sparesH = 26 + spareRows * (spareTileSize + 2) + 12;
        spareOffsetX = PAD + 80;
        spareOffsetY = MAIN_H + 22;
        spareLabelY = MAIN_H + 22 + spareTileSize / 2;
      }

      var totalH = MAIN_H + sparesH;
      el.canvas.setAttribute('viewBox', '0 0 ' + VB_W + ' ' + totalH);

      var palette = tileColorPalette();
      var primary = palette[0];
      var secondary = palette[1] || palette[0];

      // Build assignment for EVERY purchased tile (1..totalPurchased).
      // tileNum is a GLOBAL index across the whole order, so a 9×9 floor with
      // 2 spare tiles reads 1..83 sequentially (81 in the grid + 82..83 as spares).
      // packIdx still tracks which pack each tile belongs to so we can draw the
      // dark boundary line between packs.
      var allAssignment = [];
      var cursor = 0;
      packsUsed.forEach(function (pack, pi) {
        for (var i = 0; i < pack.size; i++) {
          allAssignment.push({
            packIdx: pi,
            tileNum: cursor + i + 1,
            packSize: pack.size,
            isMix: pack.isMix
          });
        }
        cursor += pack.size;
      });
      // Pad in case packs don't cover the whole grid (shouldn't normally happen).
      while (allAssignment.length < totalNeeded) {
        allAssignment.push({ packIdx: 999, tileNum: allAssignment.length + 1, packSize: 0, isMix: false });
      }
      var assignment = allAssignment.slice(0, totalNeeded);
      var spareAssignment = allAssignment.slice(totalNeeded);

      // Render.
      var html = '';
      html += '<rect x="0" y="0" width="' + VB_W + '" height="' + totalH + '" fill="#fafafa" rx="4"/>';

      var MAX_VISIBLE = 400;
      var totalTiles = cols * rows;
      var showLabels = tileSize > 16 && totalTiles <= MAX_VISIBLE;
      var showTilesIndividually = totalTiles <= MAX_VISIBLE * 2;

      if (showTilesIndividually) {
        // Pass 1 — every tile with the same neutral border + the per-pack number.
        for (var r = 0; r < rows; r++) {
          for (var c = 0; c < cols; c++) {
            var idx = r * cols + c;
            var info = assignment[idx];
            var x = offsetX + c * tileSize;
            var y = offsetY + r * tileSize;
            // When the selected color has two tones (e.g. "Blue & Teal"), render the
            // floor as a checkerboard so it matches how the tiles actually install.
            var tileColor = palette.length > 1
              ? (((r + c) % 2 === 0) ? primary : secondary)
              : primary;
            html += '<rect x="' + x + '" y="' + y + '" width="' + tileSize + '" height="' + tileSize +
              '" fill="' + tileColor + '" stroke="rgba(0,0,0,0.18)" stroke-width="' + Math.max(0.4, tileSize * 0.025) + '"/>';
            if (showLabels && info) {
              var fontSize = Math.max(8, tileSize * 0.32);
              var textColor = isLight(tileColor) ? '#1a1a1a' : '#ffffff';
              html += '<text x="' + (x + tileSize / 2) + '" y="' + (y + tileSize / 2) +
                '" text-anchor="middle" dominant-baseline="central" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="700" font-size="' + fontSize +
                '" fill="' + textColor + '">' + info.tileNum + '</text>';
            }
          }
        }
        // Pass 2 — draw a thicker dark line on every edge that separates two packs.
        var boundaryW = Math.max(1.2, tileSize * 0.06);
        for (var rr = 0; rr < rows; rr++) {
          for (var cc = 0; cc < cols; cc++) {
            var pIdx = rr * cols + cc;
            var pInfo = assignment[pIdx];
            if (!pInfo) continue;
            var px = offsetX + cc * tileSize;
            var py = offsetY + rr * tileSize;
            // Right neighbor.
            if (cc < cols - 1) {
              var rN = assignment[rr * cols + (cc + 1)];
              if (rN && rN.packIdx !== pInfo.packIdx) {
                html += '<line x1="' + (px + tileSize) + '" y1="' + py + '" x2="' + (px + tileSize) + '" y2="' + (py + tileSize) + '" stroke="#1a1a1a" stroke-width="' + boundaryW + '" stroke-linecap="square"/>';
              }
            }
            // Bottom neighbor.
            if (rr < rows - 1) {
              var bN = assignment[(rr + 1) * cols + cc];
              if (bN && bN.packIdx !== pInfo.packIdx) {
                html += '<line x1="' + px + '" y1="' + (py + tileSize) + '" x2="' + (px + tileSize) + '" y2="' + (py + tileSize) + '" stroke="#1a1a1a" stroke-width="' + boundaryW + '" stroke-linecap="square"/>';
              }
            }
          }
        }
      } else {
        // Too many tiles to render individually — show solid block + grid lines.
        html += '<rect x="' + offsetX + '" y="' + offsetY + '" width="' + gridW + '" height="' + gridH + '" fill="' + primary + '"/>';
        for (var cc = 0; cc <= cols; cc++) {
          var lx = offsetX + cc * tileSize;
          html += '<line x1="' + lx + '" y1="' + offsetY + '" x2="' + lx + '" y2="' + (offsetY + gridH) + '" stroke="rgba(0,0,0,0.15)" stroke-width="0.3"/>';
        }
        for (var rr = 0; rr <= rows; rr++) {
          var ly = offsetY + rr * tileSize;
          html += '<line x1="' + offsetX + '" y1="' + ly + '" x2="' + (offsetX + gridW) + '" y2="' + ly + '" stroke="rgba(0,0,0,0.15)" stroke-width="0.3"/>';
        }
      }

      // Outline around the main grid.
      html += '<rect x="' + (offsetX - 1) + '" y="' + (offsetY - 1) + '" width="' + (gridW + 2) + '" height="' + (gridH + 2) +
        '" fill="none" stroke="#1a1a1a" stroke-width="1" rx="2"/>';

      // Spares strip — render leftover tiles below the main grid.
      if (spareCount > 0) {
        // Divider + label.
        html += '<line x1="' + PAD + '" y1="' + (MAIN_H + 4) + '" x2="' + (VB_W - PAD) + '" y2="' + (MAIN_H + 4) + '" stroke="rgba(0,0,0,0.15)" stroke-width="0.5"/>';
        html += '<text x="' + PAD + '" y="' + (MAIN_H + 22) + '" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="700" font-size="11" fill="#6c6c6c" letter-spacing="1.2">+' + spareCount + ' SPARE' + (spareCount === 1 ? '' : 'S') + '</text>';

        // Spare tiles.
        var spareBoundaryW = Math.max(1, spareTileSize * 0.06);
        for (var si = 0; si < spareAssignment.length; si++) {
          var sInfo = spareAssignment[si];
          var sRow = Math.floor(si / spareCols);
          var sCol = si % spareCols;
          var sx = spareOffsetX + sCol * (spareTileSize + 2);
          var sy = spareOffsetY + sRow * (spareTileSize + 2);
          // Determine its grid-position color in checkerboard if multi-tone.
          // For spares we just use the primary; visually they stand alone.
          var sColor = palette.length > 1 ? (((sRow + sCol) % 2 === 0) ? primary : secondary) : primary;
          html += '<rect x="' + sx + '" y="' + sy + '" width="' + spareTileSize + '" height="' + spareTileSize +
            '" fill="' + sColor + '" stroke="#1a1a1a" stroke-width="' + spareBoundaryW + '" rx="2"/>';
          if (spareTileSize > 16) {
            var sFontSize = Math.max(8, spareTileSize * 0.35);
            var sTextColor = isLight(sColor) ? '#1a1a1a' : '#ffffff';
            html += '<text x="' + (sx + spareTileSize / 2) + '" y="' + (sy + spareTileSize / 2) +
              '" text-anchor="middle" dominant-baseline="central" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="700" font-size="' + sFontSize +
              '" fill="' + sTextColor + '">' + sInfo.tileNum + '</text>';
          }
        }
      }

      el.canvas.innerHTML = html;
    }

    function isLight(hex) {
      // Quick luminance check on a hex color for choosing label color.
      if (!hex || hex.charAt(0) !== '#') return false;
      var c = hex.length === 4
        ? hex.replace(/^#/, '').split('').map(function (ch) { return parseInt(ch + ch, 16); })
        : [parseInt(hex.substr(1, 2), 16), parseInt(hex.substr(3, 2), 16), parseInt(hex.substr(5, 2), 16)];
      var lum = (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;
      return lum > 0.6;
    }

    function renderLegend(packsUsed) {
      if (!el.legend) return;
      if (!packsUsed || packsUsed.length === 0) { el.legend.innerHTML = ''; return; }
      var palette = tileColorPalette();
      var html = '';
      var swatchBg = palette.length > 1
        ? 'linear-gradient(135deg, ' + palette[0] + ' 50%, ' + palette[1] + ' 50%)'
        : palette[0];
      packsUsed.forEach(function (p, i) {
        html += '<span class="if-calc__legend-item">' +
          '<span class="if-calc__legend-swatch" style="background:' + swatchBg + ';"></span>' +
          'Box ' + (i + 1) + ' — ' + p.size + ' tiles' +
          '</span>';
      });
      el.legend.innerHTML = html;
    }

    function renderResults() {
      var grid = getTileGrid();
      var tilesNeeded = grid.total;
      var packs = packsForCurrentColor();
      var hasPricing = packs.length > 0;
      var best = findBestCombo(tilesNeeded, packs);
      lastRecommendation = best.combo || {};

      // Resolve active quantities — manual override wins if set, otherwise use the optimizer.
      var activeQty = {};
      packs.forEach(function (p) {
        if (state.manualOverride) activeQty[p.size] = state.manualOverride[p.size] || 0;
        else activeQty[p.size] = (best.combo && best.combo[p.size]) || 0;
      });

      // Build active packsUsed (largest pack first, repeated by quantity).
      var activePacksUsed = [];
      var activeTotalTiles = 0;
      var activePrice = 0;
      packs.slice().sort(function (a, b) { return b.size - a.size; }).forEach(function (p) {
        var n = activeQty[p.size] || 0;
        for (var i = 0; i < n; i++) {
          activePacksUsed.push(p);
          activeTotalTiles += p.size;
          activePrice += p.price;
        }
      });
      var anyBox = activePacksUsed.length > 0;
      var waste = activeTotalTiles - tilesNeeded;

      // Stash for onCta.
      currentActivePacks = activePacksUsed;

      renderCanvas(grid.cols, grid.rows, activePacksUsed);
      renderLegend(activePacksUsed);

      var w = parseFloat(state.width) || 0;
      var l = parseFloat(state.length) || 0;
      var unitLabel = state.unit === 'metric' ? 'm' : 'ft';
      if (el.vizDim) el.vizDim.textContent = w.toFixed(1) + ' × ' + l.toFixed(1) + ' ' + unitLabel;
      if (el.vizGrid) el.vizGrid.textContent = grid.cols + ' × ' + grid.rows + ' tiles';

      if (el.totalTiles) el.totalTiles.textContent = activeTotalTiles;
      if (el.totalExtra) {
        if (activeTotalTiles === 0) el.totalExtra.textContent = '';
        else if (waste === 0) el.totalExtra.textContent = 'Exact fit — no waste';
        else if (waste > 0) el.totalExtra.textContent = '+' + waste + ' extra tile' + (waste === 1 ? '' : 's') + ' (spares)';
        else el.totalExtra.textContent = Math.abs(waste) + ' tile' + (waste === -1 ? '' : 's') + ' short — add more packs';
      }

      var areaM2 = activeTotalTiles * tileM * tileM;
      if (el.area && el.areaUnit) {
        if (state.unit === 'metric') {
          el.area.textContent = areaM2.toFixed(2);
          el.areaUnit.textContent = 'm²';
        } else {
          el.area.textContent = (areaM2 * M_TO_FT * M_TO_FT).toFixed(1);
          el.areaUnit.textContent = 'ft²';
        }
      }
      if (el.grid) el.grid.textContent = grid.cols + ' × ' + grid.rows;

      if (el.colorHint) el.colorHint.textContent = state.color || '';

      // Box list with ± controls — one row per available pack size.
      var html = '';
      if (packs.length === 0) {
        html = '<div class="if-calc__box-item is-zero">No packs available for this color</div>';
      } else {
        packs.forEach(function (p) {
          var qty = activeQty[p.size] || 0;
          var zero = qty === 0;
          html += '<div class="if-calc__box-item' + (zero ? ' is-zero' : '') + '">' +
            '<button type="button" class="if-calc__qty-btn" data-pack-action="dec" data-size="' + p.size + '"' + (qty === 0 ? ' disabled' : '') + ' aria-label="Remove one ' + p.size + '-Pack">−</button>' +
            '<span class="if-calc__qty-value">' + qty + '</span>' +
            '<button type="button" class="if-calc__qty-btn" data-pack-action="inc" data-size="' + p.size + '" aria-label="Add one ' + p.size + '-Pack">+</button>' +
            '<span style="margin-left:4px;">' + p.size + '-Pack</span>' +
            '</div>';
        });
      }
      if (el.boxList) {
        el.boxList.innerHTML = html;
        el.boxList.querySelectorAll('[data-pack-action]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            onPackAdjust(btn.getAttribute('data-pack-action'), parseInt(btn.getAttribute('data-size'), 10));
          });
        });
      }
      if (el.reset) el.reset.classList.toggle('is-visible', !!state.manualOverride);

      if (hasPricing && anyBox && el.priceTotal && el.priceTotalValue) {
        el.priceTotal.style.display = 'flex';
        el.priceTotalValue.textContent = formatMoney(activePrice, config.moneyFormat);
      } else if (el.priceTotal) {
        el.priceTotal.style.display = 'none';
      }

      if (el.footnote) {
        var note = 'Tiles snap together — start small and expand anytime.';
        if (waste < 0 && anyBox) note = Math.abs(waste) + ' tile' + (waste === -1 ? '' : 's') + ' short to fit your area — add more packs above.';
        else if (tilesNeeded > 200) note = 'Big floor coming up. We mixed pack sizes to keep waste low.';
        else if (waste === 0 && anyBox) note = 'Perfect fit — this combo covers your area exactly.';
        else if (waste > 0 && waste <= 2 && anyBox) note = 'Tight fit — just ' + waste + ' spare tile' + (waste === 1 ? '' : 's') + '.';
        el.footnote.textContent = note;
      }

      if (el.cta) {
        var hasCartableItems = activePacksUsed.some(function (p) { return p && p.id; });
        el.cta.disabled = !hasCartableItems;
      }
    }

    function onPackAdjust(action, size) {
      // Seed manualOverride from the current optimizer recommendation the
      // first time the user touches a +/-. After that, it tracks the user.
      if (!state.manualOverride) {
        state.manualOverride = {};
        Object.keys(lastRecommendation || {}).forEach(function (k) {
          state.manualOverride[k] = lastRecommendation[k] || 0;
        });
      }
      if (action === 'inc') {
        state.manualOverride[size] = (state.manualOverride[size] || 0) + 1;
      } else if (action === 'dec') {
        state.manualOverride[size] = Math.max(0, (state.manualOverride[size] || 0) - 1);
      }
      renderResults();
    }

    function onResetClick() {
      state.manualOverride = null;
      renderResults();
    }

    function onUnitChange(newUnit) {
      if (newUnit === state.unit) return;
      var w = parseFloat(state.width) || 0;
      var l = parseFloat(state.length) || 0;
      if (newUnit === 'imperial') {
        state.width = +(w * M_TO_FT).toFixed(2);
        state.length = +(l * M_TO_FT).toFixed(2);
      } else {
        state.width = +(w / M_TO_FT).toFixed(2);
        state.length = +(l / M_TO_FT).toFixed(2);
      }
      state.unit = newUnit;
      state.manualOverride = null;
      if (el.width) el.width.value = state.width;
      if (el.length) el.length.value = state.length;
      el.unitBtns.forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-unit') === newUnit); });
      el.unitLabels.forEach(function (u) { u.textContent = newUnit === 'metric' ? 'm' : 'ft'; });
      if (el.width) el.width.step = newUnit === 'metric' ? '0.1' : '0.5';
      if (el.length) el.length.step = newUnit === 'metric' ? '0.1' : '0.5';
      el.presetGroups.forEach(function (g) { g.style.display = g.getAttribute('data-presets-for') === newUnit ? '' : 'none'; });
      renderResults();
    }

    function onDimensionInput() {
      state.width = parseFloat(el.width.value) || 0;
      state.length = parseFloat(el.length.value) || 0;
      state.manualOverride = null;
      renderResults();
    }

    function onPresetClick(e) {
      var p = e.currentTarget.getAttribute('data-preset');
      var parts = p.split(',').map(Number);
      state.width = parts[0]; state.length = parts[1];
      state.manualOverride = null;
      if (el.width) el.width.value = state.width;
      if (el.length) el.length.value = state.length;
      renderResults();
    }

    function onColorChange(c) {
      state.color = c;
      state.manualOverride = null;
      el.colorGrid.querySelectorAll('.if-calc__swatch').forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-color') === c);
      });
      renderResults();
    }

    function showError(msg) { if (el.error) { el.error.textContent = msg; el.error.classList.add('is-visible'); } }
    function clearError() { if (el.error) { el.error.textContent = ''; el.error.classList.remove('is-visible'); } }

    function onCta() {
      clearError();
      if (!currentActivePacks || currentActivePacks.length === 0) {
        showError('Pick a color and area first, then adjust packs as needed.');
        return;
      }
      var counts = {};
      currentActivePacks.forEach(function (p) {
        if (!p || !p.id) return;
        counts[p.id] = (counts[p.id] || 0) + 1;
      });
      var items = Object.keys(counts).map(function (id) { return { id: parseInt(id, 10), quantity: counts[id] }; });
      if (items.length === 0) {
        showError('Could not match selected packs to live variants.');
        return;
      }

      var origLabel = el.cta.textContent;
      el.cta.disabled = true;
      el.cta.textContent = 'Adding to cart…';

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          return r.json().then(function (b) {
            if (!r.ok) return Promise.reject(b);
            return b;
          });
        })
        .then(function () {
          if (cartAction === 'redirect') { window.location.href = '/cart'; return; }
          if (cartAction === 'event' || cartAction === 'drawer') {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:build'));
          }
          if (cartAction === 'drawer') {
            var drawer = document.querySelector('cart-drawer, cart-notification, #CartDrawer, .cart-drawer, [data-cart-drawer]');
            if (drawer) {
              if (typeof drawer.open === 'function') drawer.open();
              else if (drawer.classList) drawer.classList.add('active', 'is-active', 'open');
            } else {
              window.location.href = '/cart';
              return;
            }
          }
          el.cta.disabled = false;
          el.cta.textContent = origLabel;
        })
        .catch(function (err) {
          console.error('[if-calc] cart/add failed', err);
          showError((err && (err.description || err.message)) || 'Could not add to cart.');
          el.cta.disabled = false;
          el.cta.textContent = origLabel;
        });
    }

    // Wire it up.
    el.unitBtns.forEach(function (b) {
      b.addEventListener('click', function () { onUnitChange(b.getAttribute('data-unit')); });
    });
    if (el.width) el.width.addEventListener('input', onDimensionInput);
    if (el.length) el.length.addEventListener('input', onDimensionInput);
    el.presets.forEach(function (p) { p.addEventListener('click', onPresetClick); });
    if (el.cta) el.cta.addEventListener('click', onCta);
    if (el.reset) el.reset.addEventListener('click', onResetClick);

    renderSwatches();
    renderResults();
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-if-calc]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target; if (!t) return;
    if (t.matches && t.matches('[data-if-calc]')) init(t);
    initAll(t);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target; if (!t) return;
    var ns = t.querySelectorAll ? t.querySelectorAll('[data-if-calc]') : [];
    ns.forEach(function (n) { n.removeAttribute('data-if-calc-ready'); });
  });
})();
